import React, { useState, useEffect, useMemo } from 'react';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Plus, Pencil, Trash2, Building2, X, Search } from 'lucide-react';
import { toast } from 'sonner';
import { CargaMasivaButton } from '../components/CargaMasivaButton';

export const EmpresasPage = () => {
  const [empresas, setEmpresas] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [currentEmpresa, setCurrentEmpresa] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    nombre: '',
    direccion: '',
    telefono: '',
    email: '',
    centros_costo: []
  });
  const [newCentroCosto, setNewCentroCosto] = useState('');

  // Filtrar empresas basado en el término de búsqueda
  const filteredEmpresas = useMemo(() => {
    if (!searchTerm.trim()) return empresas;
    const term = searchTerm.toLowerCase();
    return empresas.filter(empresa => 
      empresa.nombre?.toLowerCase().includes(term) ||
      empresa.direccion?.toLowerCase().includes(term) ||
      empresa.telefono?.toLowerCase().includes(term) ||
      empresa.email?.toLowerCase().includes(term) ||
      empresa.centros_costo?.some(cc => cc.toLowerCase().includes(term))
    );
  }, [empresas, searchTerm]);

  useEffect(() => {
    loadEmpresas();
  }, []);

  const loadEmpresas = async () => {
    try {
      const response = await api.get('/empresas');
      setEmpresas(response.data);
    } catch (error) {
      toast.error('Error al cargar empresas');
    }
  };

  const handleOpenDialog = (empresa = null) => {
    if (empresa) {
      setCurrentEmpresa(empresa);
      setFormData({
        nombre: empresa.nombre,
        direccion: empresa.direccion || '',
        telefono: empresa.telefono || '',
        email: empresa.email || '',
        centros_costo: empresa.centros_costo || []
      });
    } else {
      setCurrentEmpresa(null);
      setFormData({ nombre: '', direccion: '', telefono: '', email: '', centros_costo: [] });
    }
    setNewCentroCosto('');
    setShowDialog(true);
  };

  const handleAddCentroCosto = () => {
    if (newCentroCosto.trim()) {
      setFormData({
        ...formData,
        centros_costo: [...formData.centros_costo, newCentroCosto.trim()]
      });
      setNewCentroCosto('');
    }
  };

  const handleRemoveCentroCosto = (index) => {
    setFormData({
      ...formData,
      centros_costo: formData.centros_costo.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (currentEmpresa) {
        await api.put(`/empresas/${currentEmpresa.id}`, formData);
        toast.success('Empresa actualizada');
      } else {
        await api.post('/empresas', formData);
        toast.success('Empresa creada');
      }
      await loadEmpresas();
      setShowDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setLoading(true);
    try {
      await api.delete(`/empresas/${currentEmpresa.id}`);
      toast.success('Empresa eliminada');
      await loadEmpresas();
      setShowDeleteDialog(false);
    } catch (error) {
      toast.error('Error al eliminar empresa');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="empresas-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] text-gray-900">Empresas</h1>
          <p className="text-gray-600 mt-1">Gestión de empresas clientes y centros de costo</p>
        </div>
        <div className="flex gap-2">
          <CargaMasivaButton
            endpoint="/empresas/carga-masiva"
            onSuccess={loadEmpresas}
            templateColumns={['nombre', 'direccion', 'telefono', 'email', 'centros_costo']}
            templateName="empresas"
            entityName="Empresas"
          />
          <Button onClick={() => handleOpenDialog()} data-testid="create-empresa-button">
            <Plus size={18} className="mr-2" />
            Nueva Empresa
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-6">
          {/* Buscador */}
          <div className="mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                placeholder="Buscar por nombre, dirección, teléfono, email o centro de costo..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
                data-testid="search-empresas-input"
              />
            </div>
            {searchTerm && (
              <p className="text-sm text-gray-500 mt-2">
                {filteredEmpresas.length} resultado{filteredEmpresas.length !== 1 ? 's' : ''} encontrado{filteredEmpresas.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nombre</TableHead>
                <TableHead>Dirección</TableHead>
                <TableHead>Teléfono</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Centros de Costo</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredEmpresas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-gray-500 py-8">
                    {searchTerm ? 'No se encontraron empresas con ese criterio' : 'No hay empresas registradas'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredEmpresas.map((empresa) => (
                  <TableRow key={empresa.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Building2 size={16} className="text-primary" />
                        {empresa.nombre}
                      </div>
                    </TableCell>
                    <TableCell>{empresa.direccion || '-'}</TableCell>
                    <TableCell>{empresa.telefono || '-'}</TableCell>
                    <TableCell>{empresa.email || '-'}</TableCell>
                    <TableCell>
                      {empresa.centros_costo && empresa.centros_costo.length > 0 ? (
                        <div className="flex flex-wrap gap-1">
                          {empresa.centros_costo.slice(0, 2).map((cc, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {cc}
                            </Badge>
                          ))}
                          {empresa.centros_costo.length > 2 && (
                            <Badge variant="secondary" className="text-xs">
                              +{empresa.centros_costo.length - 2}
                            </Badge>
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400 text-sm">Sin centros</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenDialog(empresa)}
                          data-testid={`edit-empresa-${empresa.id}`}
                        >
                          <Pencil size={16} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => {
                            setCurrentEmpresa(empresa);
                            setShowDeleteDialog(true);
                          }}
                          data-testid={`delete-empresa-${empresa.id}`}
                        >
                          <Trash2 size={16} className="text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl" data-testid="empresa-dialog">
          <DialogHeader>
            <DialogTitle>
              {currentEmpresa ? 'Editar Empresa' : 'Nueva Empresa'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="nombre">Nombre *</Label>
                <Input
                  id="nombre"
                  value={formData.nombre}
                  onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                  required
                  data-testid="nombre-input"
                />
              </div>

              <div className="col-span-2">
                <Label htmlFor="direccion">Dirección</Label>
                <Input
                  id="direccion"
                  value={formData.direccion}
                  onChange={(e) => setFormData({ ...formData, direccion: e.target.value })}
                  data-testid="direccion-input"
                />
              </div>

              <div>
                <Label htmlFor="telefono">Teléfono</Label>
                <Input
                  id="telefono"
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  data-testid="telefono-input"
                />
              </div>

              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="border-t pt-4">
              <Label className="text-base font-semibold">Centros de Costo</Label>
              <p className="text-sm text-gray-600 mb-3">Gestiona los centros de costo disponibles para esta empresa</p>
              
              <div className="flex gap-2 mb-3">
                <Input
                  placeholder="Ej: CC-001, Logística, etc."
                  value={newCentroCosto}
                  onChange={(e) => setNewCentroCosto(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddCentroCosto())}
                  data-testid="centro-costo-input"
                />
                <Button
                  type="button"
                  onClick={handleAddCentroCosto}
                  disabled={!newCentroCosto.trim()}
                  data-testid="add-centro-costo-button"
                >
                  <Plus size={18} className="mr-1" />
                  Agregar
                </Button>
              </div>

              {formData.centros_costo.length > 0 ? (
                <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-md max-h-40 overflow-y-auto">
                  {formData.centros_costo.map((cc, index) => (
                    <Badge key={index} variant="secondary" className="text-sm py-1.5 px-3">
                      {cc}
                      <button
                        type="button"
                        onClick={() => handleRemoveCentroCosto(index)}
                        className="ml-2 hover:text-red-600"
                      >
                        <X size={14} />
                      </button>
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-400 italic p-3 bg-gray-50 rounded-md">
                  No hay centros de costo agregados
                </p>
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading} data-testid="submit-empresa-button">
                {loading ? 'Guardando...' : 'Guardar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Eliminación</AlertDialogTitle>
            <AlertDialogDescription>
              ¿Estás seguro de eliminar la empresa <strong>{currentEmpresa?.nombre}</strong>?
              Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              disabled={loading}
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};